import { c as _c } from "react/compiler-runtime";
import { CreateGroupsPermissionToggle } from "@components/user-info/create-groups-permission-toggle";
import { SphereUpdatableAvatar } from "@components/user-info/sphere-updatable-avatar";
import { Box } from "@mui-wrapper";
import { useRedirectToAccountSettingsPage } from "@pages/account-settings/account-settings-utils";
import { AccountSettingsTabs } from "@router/route-params";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { Button, FontWeights, Tooltip } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { getMailToLink } from "@utils/email-utils";
import { UserMenuEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { border } from "@utils/ui-utils";
import { getPrettyName } from "@utils/user-utils";
import { TeamDescription } from "@pages/members/teams/team-description";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { useMediaQueryList } from "@hooks/use-media-query";
import { PALETTE_VARS } from "@styles/palette-vars";

/** Width for the user info component shown on the right side of the page */
export const userInfoWidth = "340px";
/**
 * Component that shows the information of the user in the profile overview page,
 * allows user to open profile and give create groups permission.
 */
export function UserInfo(t0) {
  const $ = _c(42);
  const {
    member,
    team
  } = t0;
  const currentUser = useAppSelector(currentUserSelector);
  const isCurrentUser = member?.identity === currentUser?.identity;
  const {
    trackAsyncEvent
  } = useTrackEvent();
  const redirectToAccountSettingsPage = useRedirectToAccountSettingsPage();
  const {
    isScreenMdOrSmaller
  } = useMediaQueryList();
  if (!member) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== member || $[2] !== team) {
    t1 = team ? team.name : getPrettyName(member);
    $[1] = member;
    $[2] = team;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const displayName = t1;
  let t2;
  if ($[4] !== redirectToAccountSettingsPage || $[5] !== trackAsyncEvent) {
    t2 = async function onEditProfileClick() {
      await trackAsyncEvent({
        name: UserMenuEvents.openAccountSettings,
        props: {
          from: "member overview"
        }
      });
      redirectToAccountSettingsPage(AccountSettingsTabs.security);
    };
    $[4] = redirectToAccountSettingsPage;
    $[5] = trackAsyncEvent;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onEditProfileClick = t2;
  const t3 = isScreenMdOrSmaller ? "100%" : userInfoWidth;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      border: `1px solid ${PALETTE_VARS.divider}`,
      width: t3,
      display: "flex",
      flexFlow: "column",
      justifyContent: "space-between"
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = isScreenMdOrSmaller ? "20px" : "40px";
  const t6 = isScreenMdOrSmaller ? "20px" : "40px";
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      paddingTop: t5,
      paddingBottom: t6
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      marginBottom: "18px",
      justifyContent: "center",
      display: "flex",
      textAlign: "center"
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== member || $[14] !== team) {
    t9 = <Box component="div" sx={t8}><SphereUpdatableAvatar member={member} team={team} /></Box>;
    $[13] = member;
    $[14] = team;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      paddingX: "10px"
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== displayName || $[18] !== member) {
    t11 = FaroCoreApiUtils.Member.isMemberActive(member) && <Box sx={{
      fontSize: FaroCoreApiUtils.Member.isMemberActive(member) ? "20px" : "14px",
      fontWeight: FontWeights.Bold,
      color: "primary.main",
      textAlign: "center"
    }}><Tooltip component="div" title={<var>{displayName}</var>}><Box component="div" sx={withEllipsis}><var>{displayName}</var></Box></Tooltip></Box>;
    $[17] = displayName;
    $[18] = member;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== member) {
    t12 = !FaroCoreApiUtils.Member.isTeam(member) && <Box component="div" sx={{
      fontSize: FaroCoreApiUtils.Member.isMemberActive(member) ? "10px" : "14px",
      color: FaroCoreApiUtils.Member.isMemberActive(member) ? "text.secondary" : PALETTE_VARS.primary.main,
      fontWeight: FaroCoreApiUtils.Member.isMemberActive(member) ? "normal" : "bold",
      textAlign: "center"
    }}><Tooltip component="div" title={<var>{getMailToLink(member.email, {
          color: "common.white"
        })}</var>}><Box component="div" sx={withEllipsis}><var>{member.email}</var></Box></Tooltip></Box>;
    $[20] = member;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== team) {
    t13 = team && <TeamDescription team={team} />;
    $[22] = team;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isCurrentUser || $[25] !== onEditProfileClick) {
    t14 = isCurrentUser && <Box sx={{
      display: "flex",
      justifyContent: "center",
      mt: "28px"
    }}><Button variant="secondary" onClick={onEditProfileClick}>Edit profile</Button></Box>;
    $[24] = isCurrentUser;
    $[25] = onEditProfileClick;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <Box component="div" sx={t10}>{t11}{t12}{t13}{t14}</Box>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t7 || $[34] !== t9) {
    t16 = <Box component="div" sx={t7}>{t9}{t15}</Box>;
    $[32] = t15;
    $[33] = t7;
    $[34] = t9;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== member) {
    t17 = member.role === "COMPANY_MANAGER" && <Box sx={{
      marginX: "24px",
      borderTop: border(PALETTE_VARS.divider),
      paddingY: "24px"
    }}><CreateGroupsPermissionToggle member={member} /></Box>;
    $[36] = member;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t16 || $[39] !== t17 || $[40] !== t4) {
    t18 = <Box component="div" sx={t4}>{t16}{t17}</Box>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t4;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
}